// Count elapsed listening time only while the media timeline advances.
export function createListeningTracker({ getUserId, now, wallNow, send, newId, load, save }) {
  const sources = new Map()
  const state = load() || { milliseconds: {}, requests: [] }
  let sending = null

  function persist() { save(state) }

  function add(uid, start, milliseconds) {
    const days = state.milliseconds[uid] ||= {}
    let cursor = start
    const end = start + milliseconds
    while (cursor < end) {
      const date = new Date(cursor)
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      const midnight = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime()
      const next = Math.min(end, midnight)
      days[key] = (days[key] || 0) + next - cursor
      cursor = next
    }
  }

  function sample(key) {
    const source = sources.get(key)
    if (!source) return
    const current = source.read()
    const time = now()
    const wall = wallNow()
    const elapsed = Math.max(0, (time - source.time) / 1000)
    const progress = current.position - source.position
    const rate = source.rate || 1
    // Seeking must never turn skipped media into listening time.
    if (source.uid && source.uid === getUserId() && progress > 0 && progress <= elapsed * rate + 0.25) {
      const milliseconds = Math.min(elapsed, progress / rate) * 1000
      add(source.uid, source.wall, milliseconds)
    }
    Object.assign(source, { time, wall, position: current.position, rate: current.rate, uid: getUserId() })
  }

  function begin(key, read) {
    if (sources.has(key)) return
    const current = read()
    sources.set(key, { read, uid: getUserId(), time: now(), wall: wallNow(), ...current })
  }

  function stop(key) {
    sample(key)
    sources.delete(key)
    persist()
  }

  function tick() {
    for (const key of sources.keys()) sample(key)
    persist()
  }

  function flush() {
    tick()
    if (sending) return sending
    const uid = getUserId()
    if (!uid) return Promise.resolve()
    const days = state.milliseconds[uid] || {}
    for (const [date, milliseconds] of Object.entries(days)) {
      let seconds = Math.floor(milliseconds / 1000)
      days[date] = milliseconds - seconds * 1000
      while (seconds > 0) {
        const durationSeconds = Math.min(seconds, 60)
        state.requests.push({ uid, date, durationSeconds, requestId: newId() })
        seconds -= durationSeconds
      }
    }
    persist()
    sending = (async () => {
      for (const request of [...state.requests]) {
        if (request.uid !== uid) continue
        if (getUserId() !== uid) break
        const { uid: owner, ...body } = request
        await send(body)
        state.requests.splice(state.requests.indexOf(request), 1)
        persist()
      }
    })().finally(() => { sending = null })
    return sending
  }

  return { begin, stop, tick, flush }
}
